import type { ReadingPosition } from './useBookUrl'
import { findScrollTargetElement } from './scroll-position-fallback'

interface UseScrollToPositionOptions {
  container: Ref<HTMLElement | null>
  scrollOffsetPx?: number
  stabilizeMs?: number
  anchorTimeoutMs?: number
}

/** Scroll to a position, polling for elements that aren't mounted yet. */
export function useScrollToPosition(options: UseScrollToPositionOptions) {
  const {
    container,
    scrollOffsetPx = 100,
    stabilizeMs = 300,
    anchorTimeoutMs = 200,
  } = options

  const isScrolling = ref(false)
  let abortController: AbortController | null = null

  function sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  function findElement(pageNumber: string, anchor?: string): HTMLElement | null {
    const containerEl = container.value
    if (!containerEl) {
      return null
    }

    return findScrollTargetElement(containerEl, pageNumber, anchor)
  }

  async function waitForElement(
    pageNumber: string,
    anchor: string | undefined,
    timeoutMs: number,
  ): Promise<HTMLElement | null> {
    const start = Date.now()
    while (Date.now() - start < timeoutMs) {
      const el = findElement(pageNumber, anchor)
      if (el) {
        return el
      }
      await new Promise(resolve => requestAnimationFrame(resolve))
    }
    return null
  }

  function scrollToElement(element: HTMLElement): void {
    const containerEl = container.value
    if (!containerEl) {
      return
    }

    const rect = element.getBoundingClientRect()
    const containerRect = containerEl.getBoundingClientRect()
    const containerScrollTop = containerEl.scrollTop

    const elementRelativeTop = rect.top - containerRect.top + containerScrollTop
    const targetScrollTop = elementRelativeTop - scrollOffsetPx
    const maxScroll = containerEl.scrollHeight - containerEl.clientHeight
    const finalScrollTop = Math.min(maxScroll, Math.max(0, targetScrollTop))

    containerEl.scrollTo({
      top: finalScrollTop,
      behavior: 'instant',
    })
  }

  /** Scroll to position. Waits for anchor briefly, then falls back to page. */
  async function scrollTo(position: ReadingPosition): Promise<void> {
    if (!import.meta.client || !position.pageNumber) {
      return
    }

    abortController?.abort()
    abortController = new AbortController()
    const signal = abortController.signal

    isScrolling.value = true

    let element: HTMLElement | null = null

    if (position.anchor) {
      element = await waitForElement(position.pageNumber, position.anchor, anchorTimeoutMs)
    }

    if (!element) {
      element = await waitForElement(position.pageNumber, undefined, 2000)
    }

    if (signal.aborted) {
      isScrolling.value = false
      return
    }

    if (element) {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!signal.aborted) {
            scrollToElement(element)
          }
          resolve()
        }))
      })

      await sleep(stabilizeMs)
    }

    if (!signal.aborted) {
      isScrolling.value = false
    }
  }

  function cancel(): void {
    abortController?.abort()
    abortController = null
    isScrolling.value = false
  }

  onBeforeUnmount(() => {
    cancel()
  })

  return {
    isScrolling: readonly(isScrolling),
    scrollTo,
  }
}
